import { Search } from "lucide-react";
import { Children, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * The width of the one choice beside a search: narrow (10rem) for a short
 * choice such as Lezen or Schrijven, medium (12rem) for a person or an
 * action, wide (14rem) for a choice of groups. Each is a full literal class,
 * so Tailwind generates it; the grid reads it as `--filter-columns`.
 */
const COLUMNS = {
  none: "",
  narrow: "[--filter-columns:10rem]",
  medium: "[--filter-columns:12rem]",
  wide: "[--filter-columns:14rem]",
} as const;

export type FilterColumns = keyof typeof COLUMNS;

/**
 * A control's width in a filter row given as a list (`columns`): narrow (10rem) for a short choice or a toggle such as "Alleen met
 * fouten", medium (12rem) for a period, a person or an action, wide (14rem)
 * for a time range or a choice of groups, auto for a control as wide as its
 * text. Below 42rem of the row's own width every control takes half the row,
 * but `mediumRow`, `wideRow` and `autoRow` (as wide as `medium`, `wide` and
 * `auto` above it) take a row of their own there, for a text too long for
 * half a phone: a period or a time range ("Afgelopen kwartaal"), "Met
 * uitzonderingen" and its count.
 */
const WIDTHS = {
  narrow: "@2xl:w-40",
  medium: "@2xl:w-48",
  wide: "@2xl:w-56",
  auto: "@2xl:w-auto",
  mediumRow: "col-span-full @2xl:w-48",
  wideRow: "col-span-full @2xl:w-56",
  autoRow: "col-span-full @2xl:w-auto",
} as const;

export type FilterWidth = keyof typeof WIDTHS;

/** The widths that take a row of their own on a phone. */
const ROW_WIDTHS: ReadonlySet<FilterWidth> = new Set(["mediumRow", "wideRow", "autoRow"]);

interface FilterSearch {
  value: string;
  onChange: (value: string) => void;
  /** The placeholder, and the box's name for screen readers: "Zoek op naam of e-mail". */
  label: string;
}

interface SearchFieldProps extends FilterSearch {
  className?: string;
}

/**
 * A filter row's search box, 40 px high (45 px on a touch screen) with the
 * magnifier inside: also for a row of its own. Its natural width is its
 * whole placeholder, so a row that wraps (`WrappingFilterBar`) moves it to a
 * line of its own before it would cut the placeholder off.
 */
export function SearchField({ value, onChange, label, className }: SearchFieldProps) {
  return (
    <div className={cn("relative min-w-0", className)}>
      {/* No height and never seen: gives the box the placeholder's width, padding and border included. */}
      <span
        aria-hidden="true"
        className="invisible block h-0 overflow-hidden border-x border-transparent pr-3 pl-9 text-sm whitespace-nowrap"
      >
        {label}
      </span>
      <Search
        aria-hidden="true"
        className="text-muted absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
      />
      <Input
        type="search"
        placeholder={label}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 pl-9 pointer-coarse:h-10"
      />
    </div>
  );
}

interface FilterBarProps {
  /** The search box, first and as wide as the row allows; leave it out for a row of choices only. */
  search?: FilterSearch;
  /**
   * The controls' widths, in order: with a search, "none" for the search
   * alone or the one choice beside it (`COLUMNS`); else a list of widths
   * (`FilterWidth`), one per control rendered, for a row that wraps.
   */
  columns: FilterColumns | readonly FilterWidth[];
  /** Whether any filter is set: the reset link shows, in the place it always keeps. */
  filtered: boolean;
  /** Clears every filter, the search included. */
  onReset: () => void;
  /** The controls: `<Select>` and `<MultiSelect>` with `size="sm"` and `className="w-full"`. */
  children?: ReactNode;
  /**
   * In a box in De Ceuster's light blue tint: the filter bar that opens a page
   * of statistics, with its period, set apart from the tables' own rows.
   */
  tinted?: boolean;
}

/** The tinted box round a page's own filter bar. */
const TINTED = "bg-accent-light rounded-lg p-3";

/** The grid, the search's cell and the reset's cell, per shape of the row. */
interface Layout {
  grid: string;
  search: string;
  reset: string;
}

/**
 * The layout of a search with at most one choice, as full literal classes:
 * - a search alone: the search and the reset on one line;
 * - a search and one choice: one line from `@xl`; below, the search above the
 *   choice with the reset beside it.
 */
function layout(count: number): Layout {
  if (count === 0) {
    return { grid: "grid-cols-[minmax(0,1fr)_auto]", search: "", reset: "justify-self-end" };
  }
  return {
    grid: "grid-cols-2 @xl:grid-cols-[minmax(0,1fr)_var(--filter-columns)_auto]",
    search: "col-span-full @xl:col-span-1",
    reset: "justify-self-end",
  };
}

/**
 * One row of filters above a table, as on the Tools page: a search, fixed-width
 * choices and "Filters wissen". Every control has a fixed width (the search
 * takes what is left) and the reset link's place is kept while no filter is
 * set, so nothing moves when a filter changes; that place is always on a line
 * with a control, so the kept place never adds an empty line. The layout
 * follows the row's own width: one line when there is room, the search on a
 * line of its own when there is less, the choices two by two on a phone. A
 * row without a search, or with two or more controls, takes a list of widths
 * and wraps (`WrappingFilterBar`):
 *
 *     <FilterBar
 *       search={{ value: search, onChange: setSearch, label: t("…") }}
 *       columns={["wide", "medium", "medium", "narrow"]}
 *       filtered={filtered}
 *       onReset={reset}
 *     >
 *       <TimeRangeFilter value={range} onChange={setRange} />
 *       <SearchMultiSelect … className="w-full" />
 *       <SearchMultiSelect … className="w-full" />
 *       <Select …><SelectTrigger size="sm" className="w-full" … /></Select>
 *     </FilterBar>
 */
export function FilterBar({
  search,
  columns,
  filtered,
  onReset,
  children,
  tinted = false,
}: FilterBarProps) {
  if (typeof columns !== "string" || !search) {
    const widths = typeof columns !== "string" ? columns : columns === "none" ? [] : [columns];
    return (
      <WrappingFilterBar
        search={search}
        widths={widths}
        filtered={filtered}
        onReset={onReset}
        tinted={tinted}
      >
        {children}
      </WrappingFilterBar>
    );
  }
  const classes = layout(columns === "none" ? 0 : 1);

  return (
    <div className={cn("@container", tinted && TINTED)}>
      <div className={cn("grid items-center gap-2", COLUMNS[columns], classes.grid)}>
        <SearchField {...search} className={classes.search} />
        {children}
        <ResetLink filtered={filtered} onReset={onReset} className={classes.reset} />
      </div>
    </div>
  );
}

/**
 * Where the reset link goes on a phone, where the controls go two by two:
 * beside an odd last control (`last`); else on the search's line (`search`);
 * else on the line of the first control that takes a line of its own (its
 * index); else, for an even number of half-line controls and nothing else, on
 * a line of its own (`last`), a shape no page uses.
 */
export function phoneResetPlace(
  hasSearch: boolean,
  widths: readonly FilterWidth[],
): "last" | "search" | number {
  // The column the next half-line control lands in: 0 at a line's start.
  let column = 0;
  for (const width of widths) {
    column = ROW_WIDTHS.has(width) ? 0 : (column + 1) % 2;
  }
  if (column === 1) {
    return "last";
  }
  if (hasSearch) {
    return "search";
  }
  const row = widths.findIndex((width) => ROW_WIDTHS.has(width));
  return row === -1 ? "last" : row;
}

interface WrappingFilterBarProps extends Omit<FilterBarProps, "columns"> {
  widths: readonly FilterWidth[];
}

/**
 * The search's cell in a wrapping row from 42rem: at least its whole
 * placeholder (`SearchField`), and all that the controls leave of its line.
 */
const WRAPPING_SEARCH = "@2xl:grow-[1000] @2xl:basis-auto";

/**
 * A filter row given as a list of widths, such as a time range, tools, people
 * and an outcome. From 42rem of the row's own width the search and the
 * controls share one line when they fit, the search taking what the controls
 * leave; when they do not, the search takes a line of its own and the
 * controls the next, wrapping further if they need to, each at its width. The
 * reset link goes at the end of the last control, as one piece with it, so it
 * never takes a line of its own: it sits at the right end of the controls'
 * last line. Below, the search on a line of its own, the controls two by two,
 * and the reset beside an odd last control, else at the end of the search's
 * line or of the first control with a line of its own (`phoneResetPlace`).
 * The widths alone decide the wrapping, so nothing moves when a filter
 * changes.
 */
function WrappingFilterBar({
  search,
  widths,
  filtered,
  onReset,
  children,
  tinted = false,
}: WrappingFilterBarProps) {
  const controls = Children.toArray(children);
  const lastIndex = controls.length - 1;
  const phone = phoneResetPlace(search !== undefined, widths.slice(0, controls.length));
  // Without a control, the reset stays on the search's line at every width.
  const searchOnly = lastIndex < 0;
  const reset = (className: string) => (
    <ResetLink filtered={filtered} onReset={onReset} className={className} />
  );

  return (
    <div className={cn("@container", tinted && TINTED)}>
      <div className="grid grid-cols-2 items-center gap-2 @2xl:flex @2xl:flex-wrap">
        {search &&
          (phone === "search" || searchOnly ? (
            <div
              className={cn(
                "col-span-full flex items-center gap-2",
                searchOnly ? "@2xl:grow" : "@2xl:contents",
              )}
            >
              <SearchField {...search} className={cn("flex-1", WRAPPING_SEARCH)} />
              {reset(searchOnly ? "" : "@2xl:hidden")}
            </div>
          ) : (
            <SearchField {...search} className={cn("col-span-full", WRAPPING_SEARCH)} />
          ))}
        {/* The controls as one piece from 42rem, which wraps below the search as a whole. */}
        {!searchOnly && (
          <div className="contents @2xl:flex @2xl:grow @2xl:flex-wrap @2xl:items-center @2xl:gap-2">
            {controls.map((child, index) => {
              const width = widths[index] ?? "medium";
              const last = index === lastIndex;
              const hostsPhoneReset = phone === index;
              if (!last && !hostsPhoneReset) {
                return (
                  <div key={index} className={cn("min-w-0", WIDTHS[width])}>
                    {child}
                  </div>
                );
              }
              if (hostsPhoneReset) {
                // A control with a line of its own on a phone, the reset at its end there.
                return (
                  <div
                    key={index}
                    className={cn(
                      "col-span-full flex items-center gap-2",
                      last ? "@2xl:grow @2xl:justify-between" : "@2xl:contents",
                    )}
                  >
                    <div className={cn("min-w-0 flex-1 @2xl:flex-none", WIDTHS[width])}>
                      {child}
                    </div>
                    {reset(last ? "" : "@2xl:hidden")}
                  </div>
                );
              }
              // The last control and the reset as one piece from 42rem, which takes the rest of
              // its line, the reset at the right end; on a phone two cells of the grid.
              return (
                <div
                  key={index}
                  className="contents @2xl:flex @2xl:grow @2xl:items-center @2xl:justify-between @2xl:gap-2"
                >
                  <div className={cn("min-w-0", WIDTHS[width])}>{child}</div>
                  {reset(phone === "last" ? "col-start-2 justify-self-end" : "hidden @2xl:block")}
                </div>
              );
            })}
          </div>
        )}
        {!search && searchOnly && reset("col-start-2 justify-self-end")}
      </div>
    </div>
  );
}

interface ResetLinkProps {
  filtered: boolean;
  onReset: () => void;
  className?: string;
}

/** "Filters wissen": always in place, hidden while no filter is set, so nothing moves when it appears. */
function ResetLink({ filtered, onReset, className }: ResetLinkProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onReset}
      disabled={!filtered}
      aria-hidden={!filtered}
      tabIndex={filtered ? undefined : -1}
      className={cn(
        "text-accent shrink-0 px-1 text-sm font-medium whitespace-nowrap hover:underline pointer-coarse:py-2.5",
        className,
        !filtered && "invisible",
      )}
    >
      {t("common.resetFilters")}
    </button>
  );
}
